import type { CSSProperties, FormEvent } from 'react'
import type { RegistrationFormElement } from 'types'
import type { Offer, PlannedOfferEditData, ClientOffer, OfferInputName } from 'types/seller'

import { useState, useEffect } from 'react'
import InputMask from 'react-input-mask'

import { useStore } from 'store'
import { fiveYearsFromNow } from 'common'
import { getInput, getValue, leaveOnlyNumbers, trimFormData } from 'utils'
import { validateOfferForm } from 'utils/pageValidators'

import ModalContent from 'components/ModalContent'
import Button from 'components/Button'
import InputLabel from 'components/InputLabel'
import Input, { PrefixedInput } from 'components/Input'
import Spinner from 'components/Spinner'
import StyledModal from 'components/layout/StyledModal'
import copy from 'copy.json'

const button = copy.common.button
const text = copy.seller.add_offer
const form_text = copy.common.form

type Props = {
  offerData: Offer | ClientOffer
  isModalOpen: boolean
  onModalClose: () => void
  refreshData: () => void
}

export default function EditPlannedOfferDetailsModal({
  offerData,
  isModalOpen,
  onModalClose,
  refreshData
}: Props) {
  const { api, fieldErrors, clearFieldErrors } = useStore()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key in OfferInputName]?: string }>({})

  const isClientData = !('id' in offerData)

  const getCity = async (zipCode: string) => {
    const input: HTMLInputElement | null = document.querySelector('[name="location_city"]')
    const city = await api.getCity(zipCode, isClientData)

    if (!input || !city) return

    input.value = city
    setErrors(state => ({ ...state, location_city: undefined }))
  }

  const closeModal = () => {
    onModalClose()
    clearFieldErrors()
  }

  const validateInput = (
    event?: FormEvent<HTMLInputElement>,
    element?: HTMLInputElement | null
  ) => {
    const input = element || event?.currentTarget
    if (!input) return
    const validation = validateOfferForm(input)
    setErrors(state => ({ ...state, [input.name]: validation }))
    return validation
  }

  const submit = async (event: FormEvent<RegistrationFormElement>) => {
    event.preventDefault()
    setLoading(true)

    const form = event.currentTarget

    const inputs = Array.from(form.elements) as HTMLInputElement[]
    const errors = inputs.reduce(
      (result, input) => ({ ...result, [input.name]: validateOfferForm(input) }),
      {}
    )

    const hasErrors = Object.values(errors).some(error => error !== undefined)

    if (hasErrors) {
      setErrors(errors)
      setLoading(false)
      return
    }

    const requestData = Array.from(trimFormData(new FormData(form))).reduce(
      (result, [name, value]) => ({ ...result, [name]: value }),
      {} as PlannedOfferEditData
    )

    requestData.installation_power = parseFloat(String(requestData.installation_power))
    requestData.minimal_contract_length = parseFloat(String(requestData.minimal_contract_length))
    requestData.maximal_contract_length = parseFloat(String(requestData.maximal_contract_length))
    requestData.energy_price = parseFloat(String(requestData.energy_price))
    requestData.location_zip_code = leaveOnlyNumbers(requestData.location_zip_code)

    requestData.version = offerData.version

    const offerId = isClientData ? offerData.business_id : offerData.id.toString()

    const data = await api.editPlannedOffer(offerId, isClientData, requestData)

    if (!data) {
      setLoading(false)
      return
    }

    setLoading(false)
    data === 'IS_CONFLICT' ? form.reset() : closeModal()
    refreshData()
  }

  useEffect(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    setErrors(fieldErrors)
  }, [fieldErrors])

  return (
    <StyledModal isOpen={isModalOpen} onClose={closeModal}>
      <ModalContent as="form" onSubmit={submit}>
        <h3>{copy.seller.offer_details.modal.edit_offer.title}</h3>

        <InputLabel>
          <span>{form_text.building_permit_number}</span>
          <span>{errors.building_permit_number}</span>
          <Input
            name="building_permit_number"
            defaultValue={offerData.building_permit_number}
            onBlur={validateInput}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.installation_power}</span>
          <span>{errors.installation_power}</span>
          <PrefixedInput
            name="installation_power"
            type="number"
            prefix={copy.common.MW}
            prefixWidth="50px"
            reverse
            step={0.01}
            min={0}
            max={1000000}
            defaultValue={offerData.installation_power}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.energy_price}</span>
          <span>{errors.energy_price}</span>
          <PrefixedInput
            name="energy_price"
            prefix={copy.common.zl}
            prefixWidth="28px"
            reverse
            type="number"
            step={0.01}
            min={1}
            max={10000}
            defaultValue={offerData.energy_price}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <InputLabel>
          <span>{form_text.production_start_date}</span>
          <span>{errors.production_start_date}</span>
          <Input
            type="date"
            name="production_start_date"
            min="1970-01-01"
            max={fiveYearsFromNow}
            defaultValue={offerData.production_start_date}
            onBlur={validateInput}
            style={{ width: '50%' }}
          />
        </InputLabel>

        <h4>{text.subheading_1}</h4>

        <InputLabel>
          <span>{form_text.location_zip_code}</span>
          <span>{errors.location_zip_code}</span>
          <InputMask
            mask="99-999"
            maskPlaceholder={null}
            defaultValue={offerData.location_zip_code}
            onInput={event => !validateInput(event) && getCity(event.currentTarget.value)}
            onBlur={validateInput}
          >
            <Input name="location_zip_code" style={{ width: '50%' }} />
          </InputMask>
        </InputLabel>

        <InputLabel>
          <span>{form_text.location_city}</span>
          <span>{errors.location_city}</span>
          <Input
            name="location_city"
            defaultValue={offerData.location_city}
            onBlur={validateInput}
          />
        </InputLabel>

        <h4>{text.subheading_2}</h4>

        <div style={{ display: 'flex' }}>
          <InputLabel style={{ width: '100%' }}>
            <span>{text.form.minimal_contract_length}</span>
            <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
              {errors.minimal_contract_length}
            </span>
            <PrefixedInput
              name="minimal_contract_length"
              type="number"
              prefix={copy.common.months_abbr}
              prefixWidth="52px"
              reverse
              min={1}
              max={240}
              defaultValue={offerData.minimal_contract_length}
              onBlur={event => {
                validateInput(event)
                getValue('maximal_contract_length') &&
                  validateInput(undefined, getInput('maximal_contract_length'))
              }}
            />
          </InputLabel>

          <InputLabel style={{ width: '100%' }}>
            <span>{text.form.maximal_contract_length}</span>
            <span style={{ '--errorMessageHeight': '45px' } as CSSProperties}>
              {errors.maximal_contract_length}
            </span>
            <PrefixedInput
              name="maximal_contract_length"
              type="number"
              prefix={copy.common.months_abbr}
              prefixWidth="52px"
              reverse
              min={1}
              max={240}
              defaultValue={offerData.maximal_contract_length}
              onBlur={event => {
                validateInput(event)
                getValue('minimal_contract_length') &&
                  validateInput(undefined, getInput('minimal_contract_length'))
              }}
            />
          </InputLabel>
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? <Spinner size={16} /> : button.save}
        </Button>

        <Button type="button" secondary onClick={closeModal}>
          {button.cancel}
        </Button>
      </ModalContent>
    </StyledModal>
  )
}
